import { makeAutoObservable, reaction, type IReactionDisposer } from 'mobx'
import { bannerStore, EnumAlertBannerVariant } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { type SubscriptionStore } from 'entities/Subscription'
import { type OrganizationStore } from 'entities/Organization'
import { type BillingStore } from 'entities/Billing'
import { type FeatureFlagsStore } from 'entities/FeatureFlags'
import { type UsersStore } from 'entities/Users'
import { AutoRechargeWidgetStore } from 'entities/Billing/ui/AutoRechargeModal'
import { BillingPath } from 'entities/ExportRouters'

export class ReminderCreditsStore {
  private _bannerId = 'add_credits'
  private _disposeAddCredits: IReactionDisposer | null = null
  private _autoRechargeWidgetStore: AutoRechargeWidgetStore

  constructor(
    private _subscriptionStore: SubscriptionStore,
    private _organizationStore: OrganizationStore,
    private _billingStore: BillingStore,
    private _featureFlagsStore: FeatureFlagsStore,
    private _usersStore: UsersStore
  ) {
    this._autoRechargeWidgetStore = new AutoRechargeWidgetStore(
      this._billingStore,
      this._subscriptionStore
    )
    makeAutoObservable(this)
  }

  get isOnTrial() {
    return this._subscriptionStore.isTrial
  }

  get isOutOfMoney() {
    const isActivePlanWithoutCredits =
      this._subscriptionStore.isPlanActive &&
      !this.isOnTrial &&
      this._organizationStore.accountCredits <= 0
    const isTrialPlanWithoutCredits = this.isOnTrial && this._organizationStore.trialCredits <= 0
    const isInactivePlanWithoutBalance =
      !this._subscriptionStore.isPlanActive && this._organizationStore.accountBalance <= 0
    return Boolean(
      isActivePlanWithoutCredits || isTrialPlanWithoutCredits || isInactivePlanWithoutBalance
    )
  }

  get noMessageSending() {
    return !this.isOnTrial && this.isOutOfMoney && this._organizationStore.isBillableNumberVendor
  }

  get isOrganizationOwner() {
    return this._organizationStore.isOrganizationOwner
  }

  get isAdminRole() {
    return this._usersStore.user?.isAdminRole
  }

  get canManageCredits() {
    return this.isOrganizationOwner || this.isAdminRole
  }

  get show() {
    return this.noMessageSending
  }

  get autoRechargeWidgetStore() {
    return this._autoRechargeWidgetStore
  }

  navigateToUpgrade = () => {
    uiStore.changeRoute({
      path: `/${BillingPath.upgrade}`,
    })
  }

  init = async () => {
    await Promise.all([
      this._organizationStore.initPromise,
      this._subscriptionStore.initPromise,
      this._usersStore.initPromise,
    ])
    this.reactionAddCredits()
  }

  reactionAddCredits = () => {
    this._disposeAddCredits?.()
    this._disposeAddCredits = reaction(
      () => ({
        show: this.show,
        canManageCredits: this.canManageCredits,
      }),
      ({ show, canManageCredits }) => {
        if (show) {
          if (canManageCredits) {
            bannerStore.add({
              id: this._bannerId,
              action: {
                text: 'Upgrade plan',
                onAction: () => {
                  uiStore.changeRoute({
                    path: `/${BillingPath.upgrade}`,
                  })
                },
              },
              secondAction: {
                text: 'Enable auto-recharge',
                onAction: () => {
                  this._autoRechargeWidgetStore.openModal()
                },
              },
              alert: {
                title:
                  'Your organization is out of credits. Upgrade or enable auto-recharge to keep sending and receiving messages.',
                variant: EnumAlertBannerVariant.Warning,
                ariaLabel: 'OutOfCredits',
              },
            })
          } else {
            bannerStore.add({
              id: this._bannerId,
              alert: {
                title:
                  'Your organization is out of credits. Ask one of your admins to upgrade or enable auto-recharge.',
                variant: EnumAlertBannerVariant.Warning,
                ariaLabel: 'OutOfCredits',
              },
            })
          }
        } else {
          bannerStore.remove(this._bannerId)
        }
      },
      {
        fireImmediately: true,
      }
    )
  }
}
